import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { trackCta, trackEvent } from "@/lib/analytics";
import { useSiteMedia } from "@/lib/useSiteMedia";
import MyStoicOsBlock from "@/components/MyStoicOsBlock";

// Fallback URLs — these are the images the Agora rendered before the
// Site Media Registry existed. They stay in the tree as a safety net;
// the useSiteMedia hook returns them when a slot is unmapped OR its
// linked asset is not in published status.
const AGORA_HERO_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/77ish8ed_IMG_4816.jpeg";
const AGORA_ZENO_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/3upcf9vr_IMG_4824.jpeg";
const AGORA_STOA_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/c6qdee2a_IMG_4839.jpeg";
const AGORA_CLEANTHES_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/moosvmjo_IMG_4844.jpeg";
const AGORA_CHRYSIPPUS_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/07f5eh9i_IMG_4846.jpeg";
const AGORA_BRIDGE_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/ip7qn2s4_IMG_4848.jpeg";
const AGORA_SENECA_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/qr72stc7_IMG_4849.jpeg";
const AGORA_EPICTETUS_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/0sryak1t_IMG_4850.jpeg";
const AGORA_MARCUS_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/knbb4o3o_IMG_4851.jpeg";
const AGORA_MARCUS_WAR_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/uxlk8mrj_IMG_4853.jpeg";
const AGORA_MARCUS_END_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/rfevimtz_IMG_4855.jpeg";

/**
 * THE AGORA — a single narrative scrolling page.
 *
 * Eleven chapters numbered with Roman numerals, separated by hairline
 * gold rules, set against deep black with subtle radial "morning light"
 * gradients. Pure typographic atmosphere — Cinzel for inscriptional
 * chapter headings, italic Cormorant Garamond for pull-quotes,
 * Crimson Text for the long-read body. No stock photos.
 *
 * Every string is bilingual via i18n (agora.*).
 */

/* --- helpers --- */

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      node.style.opacity = 1;
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            node.style.transitionDelay = `${delay}ms`;
            node.style.opacity = "1";
            node.style.transform = "translateY(0)";
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div
      ref={ref}
      style={{
        opacity: 0,
        transform: "translateY(16px)",
        transition: "opacity 1100ms ease, transform 1100ms cubic-bezier(.2,.7,.2,1)",
      }}
      className={className}
    >
      {children}
    </div>
  );
}

function Numeral({ n }) {
  return (
    <p className="font-inscription text-gold text-sm tracking-[0.4em] mb-6" data-testid={`agora-numeral-${n}`}>
      {n}
    </p>
  );
}

function Heading({ children, testid }) {
  return (
    <h2
      data-testid={testid}
      className="font-inscription text-bone text-3xl sm:text-4xl md:text-5xl tracking-[0.06em] uppercase leading-[1.05]"
    >
      {children}
    </h2>
  );
}

function Atmosphere({ children }) {
  return (
    <p className="mt-6 font-serif italic text-gold/85 text-lg md:text-xl leading-[1.5] max-w-2xl">
      {children}
    </p>
  );
}

function Body({ children }) {
  return (
    <p className="mt-8 font-reading text-bone/85 text-lg md:text-xl leading-[1.95] max-w-2xl">
      {children}
    </p>
  );
}

function Dates({ children }) {
  return (
    <p className="mt-3 text-[11px] tracking-[0.28em] uppercase text-fog/80">
      {children}
    </p>
  );
}

function Rule({ wide = false }) {
  return (
    <div
      aria-hidden
      className={`mx-auto mt-24 mb-24 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent ${wide ? "w-64" : "w-32"}`}
    />
  );
}

function Chapter({ children, idAnchor }) {
  return (
    <section id={idAnchor} className="relative">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <Reveal>{children}</Reveal>
      </div>
    </section>
  );
}

function MorningLight() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div
        className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[120vw] h-[80vh] opacity-[0.35]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(197,160,89,0.18) 0%, rgba(197,160,89,0.05) 35%, transparent 65%)",
          filter: "blur(40px)",
        }}
      />
    </div>
  );
}

/* --- cinematic scene (reusable for Scene 1 hero + Scene 2 chapter I) --- */

/**
 * A full-bleed cinematic section with a background image and overlay text.
 *
 * Responsive behaviour:
 *   • Desktop (≥ md): image fills 100svh as a true background, text overlays
 *     on the left third with subtle parallax (image 0.18×, text 0.32×).
 *   • Mobile (< md): image stacks ABOVE the text as a properly-proportioned
 *     banner — its aspect ratio is preserved so figures at the edges
 *     (Zeno on the left, Acropolis on the right) remain fully visible
 *     instead of being cropped. Text sits below on the ink-black canvas.
 *
 * Props:
 *   src         — image url
 *   alt         — accessible alt text (for the in-flow img only)
 *   focal       — object-position string (e.g. "center center", "30% 50%")
 *   mobileAR    — mobile banner aspect ratio (default "aspect-[4/5]")
 *   eyebrow     — small all-caps text above title
 *   title       — H1 (Cinzel)
 *   subtitle    — italic gold sentence below title
 *   atmosphere  — italic muted-gold mood text (used on Scene 2)
 *   body        — long-read paragraph (used on Scene 2)
 *   bodyMeta    — optional small uppercase row above body (figures/dates)
 *   triad       — optional 3-line poem (only used on Scene 1 if requested)
 *   ctaLabel    — button text
 *   onCta       — button handler
 *   scrollCue   — bottom scroll cue text
 *   testid      — data-testid root
 *   id          — section id (for anchor scrolling)
 *   headingLevel— 'h1' | 'h2'
 */
function CinematicScene({
  src,
  alt,
  focal = "center center",
  // legacy prop, retained for backwards compatibility
  mobileAR: _mobileAR = "aspect-video",
  eyebrow,
  title,
  subtitle,
  atmosphere,
  quote,
  body,
  bodyMeta,
  ctaLabel,
  onCta,
  scrollCue,
  testid,
  id,
  headingLevel = "h1",
}) {
  const [loaded, setLoaded] = useState(false);
  const { t } = useT();
  const storyRef = useRef(null);

  const HeadingTag = headingLevel;

  const hasStory =
    !!atmosphere ||
    !!bodyMeta ||
    !!body ||
    (Array.isArray(body) && body.length > 0);

  const scrollToStory = () => {
    if (onCta) {
      onCta();
      return;
    }
    storyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const titleShadow = {
    textShadow:
      "0 2px 24px rgba(0,0,0,0.85), 0 1px 6px rgba(0,0,0,0.7)",
  };
  const overlayShadow = {
    textShadow: "0 1px 12px rgba(0,0,0,0.75), 0 1px 3px rgba(0,0,0,0.6)",
  };

  return (
    <section
      id={id}
      data-testid={testid}
      className="relative w-full bg-ink"
    >
      {/* ============ CINEMATIC HERO ============ */}
      <div
        className="relative w-full overflow-hidden bg-ink"
        style={{ height: "min(100svh, 88vh)", minHeight: "560px" }}
      >
        <img
          src={src}
          alt={alt || ""}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          data-testid={`${testid}-image`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1100ms] ease-out ${loaded ? "opacity-100" : "opacity-0"}`}
          style={{ objectPosition: focal }}
        />

        {/* Subtle bottom-up readability gradient — keeps the artwork bright,
            only the lower portion is darkened for legible text overlay. */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 22%, rgba(0,0,0,0.20) 50%, rgba(0,0,0,0.05) 75%, transparent 100%)",
          }}
        />

        {/* Smooth fade into the story section below. */}
        <div
          className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
          aria-hidden
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(5,5,5,0.92) 65%, rgba(5,5,5,1))",
          }}
        />

        {/* TEXT OVERLAY
              • Mobile / tablet : pinned bottom-left (over the dark gradient)
              • Desktop (lg+)   : vertically centered, left-aligned */}
        <div className="absolute inset-0 flex items-end lg:items-center pb-16 sm:pb-20 lg:pb-0 pt-24 lg:pt-32">
          <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 xl:px-20">
            <div className="max-w-2xl">
              {eyebrow && (
                <Reveal>
                  <p
                    data-testid={`${testid}-eyebrow`}
                    className="text-[11px] tracking-[0.36em] uppercase text-gold mb-5 md:mb-7"
                    style={overlayShadow}
                  >
                    {eyebrow}
                  </p>
                </Reveal>
              )}

              {title && (
                <Reveal delay={120}>
                  <HeadingTag
                    data-testid={`${testid}-title`}
                    className="font-inscription text-bone text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] tracking-[0.08em] uppercase leading-[0.98] break-words"
                    style={titleShadow}
                  >
                    {title}
                  </HeadingTag>
                </Reveal>
              )}

              {subtitle && (
                <Reveal delay={260}>
                  <p
                    data-testid={`${testid}-subtitle`}
                    className="mt-5 md:mt-7 font-serif italic text-gold-soft text-lg sm:text-xl md:text-2xl lg:text-[1.625rem] tracking-tight leading-snug"
                    style={overlayShadow}
                  >
                    {subtitle}
                  </p>
                </Reveal>
              )}

              {quote && quote.length > 0 && (
                <Reveal delay={400}>
                  <figure className="mt-6 md:mt-8 pl-4 md:pl-5 border-l-2 border-gold/60">
                    <blockquote
                      className="space-y-1 font-serif italic text-bone text-base sm:text-lg md:text-xl leading-snug"
                      style={overlayShadow}
                    >
                      {quote.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </blockquote>
                  </figure>
                </Reveal>
              )}

              {(ctaLabel || hasStory) && (
                <Reveal delay={560}>
                  <button
                    type="button"
                    onClick={scrollToStory}
                    data-testid={`${testid}-cta`}
                    className="mt-8 md:mt-10 inline-flex items-center gap-3 px-7 md:px-8 py-3.5 md:py-4 border border-gold/70 hover:border-gold bg-black/30 hover:bg-gold/[0.10] backdrop-blur-[2px] text-[11px] tracking-[0.32em] uppercase text-bone hover:text-gold transition-all duration-500"
                    style={overlayShadow}
                  >
                    {ctaLabel || t("agora.continueJourney")}
                    <ArrowDown size={14} strokeWidth={1.4} />
                  </button>
                </Reveal>
              )}
            </div>
          </div>
        </div>

        {/* Optional explicit scroll cue (used by 'Enter the Stoa' hero) */}
        {scrollCue && (
          <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-bone/70 flex-col items-center gap-3 z-10">
            <span style={overlayShadow}>{scrollCue}</span>
            <span className="h-10 w-px bg-gradient-to-b from-gold/70 to-transparent" />
          </div>
        )}
      </div>

      {/* ============ STORY CONTENT — biography, philosophy, lessons ============ */}
      {hasStory && (
        <div
          ref={storyRef}
          data-testid={`${testid}-story`}
          className="relative w-full bg-ink"
        >
          <article className="max-w-[900px] mx-auto px-6 sm:px-10 py-16 md:py-24 lg:py-28">
            {bodyMeta && (
              <Reveal>
                <p className="text-[11px] tracking-[0.22em] uppercase text-gold/85 mb-8 md:mb-10">
                  {bodyMeta}
                </p>
              </Reveal>
            )}

            {atmosphere && (
              <Reveal delay={120}>
                <p className="font-serif italic text-gold/85 text-lg md:text-xl lg:text-2xl leading-[1.55] mb-10 md:mb-12 first-letter:text-gold first-letter:font-serif first-letter:text-[3.25rem] first-letter:leading-none first-letter:float-left first-letter:mr-3 first-letter:mt-1">
                  {atmosphere}
                </p>
              </Reveal>
            )}

            {body && (
              <Reveal delay={220}>
                {Array.isArray(body) ? (
                  <div className="space-y-6 md:space-y-7">
                    {body.map((para, idx) => (
                      <p
                        key={idx}
                        className="font-reading text-bone/90 text-base md:text-lg lg:text-[1.175rem] leading-[1.85]"
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="font-reading text-bone/90 text-base md:text-lg lg:text-[1.175rem] leading-[1.85]">
                    {body}
                  </p>
                )}
              </Reveal>
            )}
          </article>
        </div>
      )}
    </section>
  );
}

/* --- page --- */

export default function Agora() {
  const { t } = useT();

  // Site Media Registry lookups — all 11 chapter images. Each returns the
  // published Vault URL if the slot has a mapping, otherwise falls back
  // to the hard-coded URL declared above.
  const heroMedia       = useSiteMedia("agora.hero",        { fallbackUrl: AGORA_HERO_IMG });
  const zenoMedia       = useSiteMedia("agora.zeno",        { fallbackUrl: AGORA_ZENO_IMG });
  const stoaMedia       = useSiteMedia("agora.stoa",        { fallbackUrl: AGORA_STOA_IMG });
  const cleanthesMedia  = useSiteMedia("agora.cleanthes",   { fallbackUrl: AGORA_CLEANTHES_IMG });
  const chrysippusMedia = useSiteMedia("agora.chrysippus",  { fallbackUrl: AGORA_CHRYSIPPUS_IMG });
  const bridgeMedia     = useSiteMedia("agora.bridge",      { fallbackUrl: AGORA_BRIDGE_IMG });
  const senecaMedia     = useSiteMedia("agora.seneca",      { fallbackUrl: AGORA_SENECA_IMG });
  const epictetusMedia  = useSiteMedia("agora.epictetus",   { fallbackUrl: AGORA_EPICTETUS_IMG });
  const marcusMedia     = useSiteMedia("agora.marcus",      { fallbackUrl: AGORA_MARCUS_IMG });
  const marcusWarMedia  = useSiteMedia("agora.marcus.war",  { fallbackUrl: AGORA_MARCUS_WAR_IMG });
  const marcusEndMedia  = useSiteMedia("agora.marcus.end",  { fallbackUrl: AGORA_MARCUS_END_IMG });

  useSEO({
    title: `${t("agora.hero.title")}: ${t("agora.hero.subtitle")} | StoicOs`,
    description: t("agora.intro"),
    canonical: "https://stoicos.ai/agora",
    og: {
      title: `${t("agora.hero.title")}: ${t("agora.hero.subtitle")}`,
      description: t("agora.intro"),
    },
    jsonLd: [
      webPage({
        name: t("agora.hero.title"),
        description: t("agora.intro"),
        url: "/agora",
      }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: t("agora.hero.title"), url: "/agora" },
      ]),
    ],
  });

  useEffect(() => {
    trackEvent("agora_view");
  }, []);

  const scrollToFirst = () => {
    trackCta("agora_begin_journey");
    const node = document.getElementById("agora-chapter-1");
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const figureKeys = [
    "agora.fig.zeno",
    "agora.fig.cleanthes",
    "agora.fig.chrysippus",
    "agora.fig.panaetius",
    "agora.fig.posidonius",
    "agora.fig.seneca",
    "agora.fig.musonius",
    "agora.fig.epictetus",
    "agora.fig.marcus",
    "agora.fig.modern",
  ];

  const s9Topics = ["agora.s9.t1","agora.s9.t2","agora.s9.t3","agora.s9.t4","agora.s9.t5","agora.s9.t6","agora.s9.t7","agora.s9.t8"];

  return (
    <main data-testid="agora-page" className="relative bg-ink text-bone">

      {/* ============== SCENE 1 — HERO: ENTER THE STOA ============== */}
      <CinematicScene
        id="agora-hero-section"
        testid="agora-hero"
        src={heroMedia.url}
        alt={t("agora.hero.alt")}
        focal="60% center"
        mobileAR="aspect-video"
        eyebrow={t("agora.hero.eyebrow")}
        title={t("agora.hero.title")}
        subtitle={t("agora.hero.subtitle")}
        ctaLabel={t("agora.hero.cta")}
        onCta={scrollToFirst}
        scrollCue={t("agora.hero.scroll")}
        headingLevel="h1"
      />

      {/* ============== INTRO ============== */}
      <section data-testid="agora-intro" className="relative">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <p className="font-reading italic text-bone/85 text-xl md:text-2xl leading-[1.85]">
              {t("agora.intro")}
            </p>
          </Reveal>
        </div>
        <Rule wide />
      </section>

      {/* ============== SCENE 2 — I · THE GREEK DAWN (Zeno teaching) ============== */}
      <section id="agora-chapter-1" aria-label={t("agora.s1.title")}>
        <CinematicScene
          testid="agora-s1"
          src={zenoMedia.url}
          alt={t("agora.s1.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.s1.numeral")} · ${t("agora.s1.dates")}`}
          title={t("agora.s1.title")}
          subtitle={t("agora.s1.atmosphere")}
          bodyMeta={t("agora.s1.figures")}
          body={t("agora.s1.body")}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 3 — II · THE STOA POIKILE (school of reason) ============== */}
      <section id="agora-chapter-2" aria-label={t("agora.stoa.title")}>
        <CinematicScene
          testid="agora-stoa"
          src={stoaMedia.url}
          alt={t("agora.stoa.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.stoa.numeral")} · ${t("agora.stoa.subtitle")}`}
          title={t("agora.stoa.title")}
          subtitle={t("agora.stoa.atmosphere")}
          quote={[
            t("agora.stoa.q1"),
            t("agora.stoa.q2"),
            t("agora.stoa.q3"),
            t("agora.stoa.q4"),
            t("agora.stoa.q5"),
          ]}
          body={[
            t("agora.stoa.p1"),
            t("agora.stoa.p2"),
            t("agora.stoa.p3"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 4 — III · CLEANTHES (discipline & devotion) ============== */}
      <section id="agora-chapter-3" aria-label={t("agora.cleanthes.title")}>
        <CinematicScene
          testid="agora-cleanthes"
          src={cleanthesMedia.url}
          alt={t("agora.cleanthes.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.cleanthes.numeral")} · ${t("agora.cleanthes.dates")}`}
          title={t("agora.cleanthes.title")}
          subtitle={t("agora.cleanthes.subtitle")}
          bodyMeta={t("agora.cleanthes.virtues")}
          quote={[
            t("agora.cleanthes.q1"),
            t("agora.cleanthes.q2"),
          ]}
          body={[
            t("agora.cleanthes.p1"),
            t("agora.cleanthes.p2"),
            t("agora.cleanthes.p3"),
            t("agora.cleanthes.p4"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 5 — V · CHRYSIPPUS (system of reason) ============== */}
      <section id="agora-chapter-4" aria-label={t("agora.chrysippus.title")}>
        <CinematicScene
          testid="agora-chrysippus"
          src={chrysippusMedia.url}
          alt={t("agora.chrysippus.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.chrysippus.numeral")} · ${t("agora.chrysippus.dates")}`}
          title={t("agora.chrysippus.title")}
          subtitle={t("agora.chrysippus.subtitle")}
          bodyMeta={t("agora.chrysippus.concepts")}
          quote={[
            t("agora.chrysippus.q1"),
            t("agora.chrysippus.q2"),
          ]}
          body={[
            t("agora.chrysippus.p1"),
            t("agora.chrysippus.p2"),
            t("agora.chrysippus.p3"),
            t("agora.chrysippus.p4"),
            t("agora.chrysippus.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 6 — VI · THE BRIDGE TO ROME (Panaetius) ============== */}
      <section id="agora-chapter-5" aria-label={t("agora.bridge.title")}>
        <CinematicScene
          testid="agora-bridge"
          src={bridgeMedia.url}
          alt={t("agora.bridge.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.bridge.numeral")} · ${t("agora.bridge.dates")}`}
          title={t("agora.bridge.title")}
          subtitle={t("agora.bridge.subtitle")}
          bodyMeta={t("agora.bridge.concepts")}
          quote={[
            t("agora.bridge.q1"),
            t("agora.bridge.q2"),
          ]}
          body={[
            t("agora.bridge.p1"),
            t("agora.bridge.p2"),
            t("agora.bridge.p3"),
            t("agora.bridge.p4"),
            t("agora.bridge.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 7 — VII · SENECA (wisdom amid power) ============== */}
      <section id="agora-chapter-6" aria-label={t("agora.seneca.title")}>
        <CinematicScene
          testid="agora-seneca"
          src={senecaMedia.url}
          alt={t("agora.seneca.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.seneca.numeral")} · ${t("agora.seneca.dates")}`}
          title={t("agora.seneca.title")}
          subtitle={t("agora.seneca.subtitle")}
          bodyMeta={t("agora.seneca.concepts")}
          quote={[
            t("agora.seneca.q1"),
            t("agora.seneca.q2"),
            t("agora.seneca.q3"),
            t("agora.seneca.qattr"),
          ]}
          body={[
            t("agora.seneca.p1"),
            t("agora.seneca.p2"),
            t("agora.seneca.p3"),
            t("agora.seneca.p4"),
            t("agora.seneca.p5"),
            t("agora.seneca.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 8 — VIII · EPICTETUS (the freedom within) ============== */}
      <section id="agora-chapter-7" aria-label={t("agora.epictetus.title")}>
        <CinematicScene
          testid="agora-epictetus"
          src={epictetusMedia.url}
          alt={t("agora.epictetus.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.epictetus.numeral")} · ${t("agora.epictetus.dates")}`}
          title={t("agora.epictetus.title")}
          subtitle={t("agora.epictetus.subtitle")}
          bodyMeta={t("agora.epictetus.concepts")}
          quote={[
            t("agora.epictetus.q1"),
            t("agora.epictetus.qattr"),
          ]}
          body={[
            t("agora.epictetus.p1"),
            t("agora.epictetus.p2"),
            t("agora.epictetus.p3"),
            t("agora.epictetus.p4"),
            t("agora.epictetus.p5"),
            t("agora.epictetus.p6"),
            t("agora.epictetus.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule />

      {/* ============== SCENE 9 — IX · MARCUS AURELIUS (the philosopher) ============== */}
      <section id="agora-chapter-8" aria-label={t("agora.marcus.title")}>
        <CinematicScene
          testid="agora-marcus"
          src={marcusMedia.url}
          alt={t("agora.marcus.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.marcus.numeral")} · ${t("agora.marcus.dates")}`}
          title={t("agora.marcus.title")}
          subtitle={t("agora.marcus.subtitle")}
          bodyMeta={t("agora.marcus.concepts")}
          quote={[
            t("agora.marcus.q1"),
            t("agora.marcus.q2"),
            t("agora.marcus.qattr"),
          ]}
          body={[
            t("agora.marcus.p1"),
            t("agora.marcus.p2"),
            t("agora.marcus.p3"),
            t("agora.marcus.p4"),
            t("agora.marcus.p5"),
            t("agora.marcus.p6"),
            t("agora.marcus.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule wide />

      {/* ============== SCENE 10 — X · MARCUS AURELIUS — THE PHILOSOPHER IN WAR ============== */}
      <section id="agora-chapter-9" aria-label={t("agora.marcusWar.title")}>
        <CinematicScene
          testid="agora-marcus-war"
          src={marcusWarMedia.url}
          alt={t("agora.marcusWar.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.marcusWar.numeral")} · ${t("agora.marcusWar.dates")}`}
          title={t("agora.marcusWar.title")}
          subtitle={t("agora.marcusWar.subtitle")}
          bodyMeta={t("agora.marcusWar.concepts")}
          quote={[
            t("agora.marcusWar.q1"),
            t("agora.marcusWar.q2"),
            t("agora.marcusWar.qattr"),
          ]}
          body={[
            t("agora.marcusWar.p1"),
            t("agora.marcusWar.p2"),
            t("agora.marcusWar.p3"),
            t("agora.marcusWar.p4"),
            t("agora.marcusWar.p5"),
            t("agora.marcusWar.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule wide />

      {/* ============== SCENE 11 — XI · MARCUS AURELIUS — THE LAST SUNSET ============== */}
      <section id="agora-chapter-marcus-end" aria-label={t("agora.marcusEnd.title")}>
        <CinematicScene
          testid="agora-marcus-end"
          src={marcusEndMedia.url}
          alt={t("agora.marcusEnd.alt")}
          focal="50% center"
          mobileAR="aspect-video"
          eyebrow={`${t("agora.marcusEnd.numeral")} · ${t("agora.marcusEnd.dates")}`}
          title={t("agora.marcusEnd.title")}
          subtitle={t("agora.marcusEnd.subtitle")}
          bodyMeta={t("agora.marcusEnd.concepts")}
          quote={[
            t("agora.marcusEnd.q1"),
            t("agora.marcusEnd.q2"),
            t("agora.marcusEnd.q3"),
            t("agora.marcusEnd.qattr"),
          ]}
          body={[
            t("agora.marcusEnd.p1"),
            t("agora.marcusEnd.p2"),
            t("agora.marcusEnd.p3"),
            t("agora.marcusEnd.p4"),
            t("agora.marcusEnd.p5"),
            t("agora.marcusEnd.p6"),
            t("agora.marcusEnd.p7"),
            t("agora.marcusEnd.p8"),
            t("agora.marcusEnd.closing"),
            t("agora.marcusEnd.note"),
          ]}
          headingLevel="h2"
        />
      </section>
      <Rule wide />

      {/* ============== XII — THIS I WISH TO LEARN (invitation to My StoicOs) ============== */}
      <section id="agora-chapter-next" data-testid="agora-next" className="relative">
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-32 md:pt-44 text-center">
          <Reveal>
            <Heading testid="agora-next-title">{t("agora.next.title")}</Heading>
            <div className="mt-12 space-y-6 font-reading text-bone/85 text-lg leading-[1.9]">
              <p>{t("agora.next.p1")}</p>
              <p>{t("agora.next.p2")}</p>
              <p>{t("agora.next.p3")}</p>
              <p>{t("agora.next.p4")}</p>
            </div>
          </Reveal>
        </div>
        <div className="pb-10 md:pb-16 -mt-4">
          <MyStoicOsBlock trackEvent="agora_mystoicos_cta" />
        </div>
      </section>
      <Rule />

      {/* ============== XIII — THE HALL OF LIGHT ============== */}
      <section id="agora-chapter-10" data-testid="agora-s8" className="relative">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background:
              "radial-gradient(circle at 50% 30%, rgba(232,200,121,0.10) 0%, transparent 55%)",
          }}
        />
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-40 md:py-56 text-center relative">
          <Reveal>
            <Numeral n={t("agora.s8.numeral")} />
            <Heading testid="agora-s8-title">{t("agora.s8.title")}</Heading>
            <p className="mt-10 font-serif italic text-gold/80 text-lg leading-[1.6]">
              {t("agora.s8.atmosphere")}
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-20 space-y-4 font-serif text-bone/95 text-2xl md:text-3xl leading-snug">
              <p>{t("agora.s8.line1")}</p>
              <p>{t("agora.s8.line2")}</p>
              <p>{t("agora.s8.line3")}</p>
              <p className="text-gold italic mt-8">{t("agora.s8.line4")}</p>
            </div>
          </Reveal>

          <div className="mt-20 w-24 h-px mx-auto bg-gold/40" aria-hidden />

          <Reveal delay={500}>
            <div className="mt-20 space-y-4 font-serif text-bone/95 text-2xl md:text-3xl leading-snug">
              <p>{t("agora.s8.line5")}</p>
              <p className="text-gold italic">{t("agora.s8.line6")}</p>
            </div>
          </Reveal>

          <div className="mt-20 w-24 h-px mx-auto bg-gold/40" aria-hidden />

          <Reveal delay={700}>
            <div className="mt-20 space-y-4 font-serif text-bone/95 text-2xl md:text-3xl leading-snug">
              <p>{t("agora.s8.line7")}</p>
              <p className="text-gold italic">{t("agora.s8.line8")}</p>
            </div>
          </Reveal>
        </div>
      </section>
      <Rule wide />

      {/* ============== XIV — THE MODERN STOA ============== */}
      <Chapter idAnchor="agora-chapter-11">
        <Numeral n={t("agora.s9.numeral")} />
        <Heading testid="agora-s9-title">{t("agora.s9.title")}</Heading>
        <Atmosphere>{t("agora.s9.atmosphere")}</Atmosphere>
        <Body>{t("agora.s9.body")}</Body>
        <p className="mt-14 text-[11px] tracking-[0.3em] uppercase text-gold mb-5">
          {t("agora.s9.topicsLabel")}
        </p>
        <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {s9Topics.map((k) => (
            <li
              key={k}
              data-testid={`agora-s9-topic-${k.split(".").pop()}`}
              className="px-4 py-3 border border-white/10 text-center text-[11px] tracking-[0.18em] uppercase text-bone/80"
            >
              {t(k)}
            </li>
          ))}
        </ul>
      </Chapter>
      <Rule />

      {/* ============== XV — VIRTUE GUIDE (transition) ============== */}
      <section id="agora-chapter-12" data-testid="agora-s10" className="relative">
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-32 md:py-40 text-center">
          <Reveal>
            <Numeral n={t("agora.s10.numeral")} />
            <Heading testid="agora-s10-title">{t("agora.s10.title")}</Heading>
            <p className="mt-10 font-serif italic text-bone/90 text-2xl md:text-3xl leading-snug">
              {t("agora.s10.body")}
            </p>
            <Link
              to="/#download"
              onClick={() => trackCta("agora_open_virtue_guide")}
              data-testid="agora-s10-cta"
              className="mt-12 inline-flex items-center gap-3 px-9 py-4 bg-gold/[0.08] hover:bg-gold/[0.16] border border-gold/70 hover:border-gold text-[12px] tracking-[0.28em] uppercase text-gold hover:text-gold-hover transition-all duration-500"
            >
              {t("agora.s10.cta")}
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <p className="mt-10 font-serif italic text-gold/80 text-lg">
              {t("agora.s10.note")}
            </p>
          </Reveal>
        </div>
      </section>
      <Rule wide />

      {/* ============== Future chapters expandable ============== */}
      <section data-testid="agora-future" className="relative">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24">
          <Reveal>
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">
              {t("agora.figures.label")}
            </p>
            <p className="font-reading text-bone/80 text-lg leading-[1.85] max-w-2xl">
              {t("agora.figures.note")}
            </p>
            <ul className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3">
              {figureKeys.map((k) => (
                <li
                  key={k}
                  data-testid={`agora-figure-${k.split(".").pop()}`}
                  className="px-5 py-5 border border-white/8 hover:border-gold/30 hover:bg-white/[0.02] transition-colors duration-500"
                >
                  <span className="font-serif text-xl text-bone block leading-tight">
                    {t(k)}
                  </span>
                  <span className="mt-2 block text-[10px] tracking-[0.28em] uppercase text-gold/70">
                    {t("agora.placeholder")}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
      <Rule wide />

      {/* ============== Philosophy ============== */}
      <section data-testid="agora-philosophy" className="relative">
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">
              {t("agora.philosophy.title")}
            </p>
            <p className="font-reading italic text-bone/85 text-xl md:text-2xl leading-[1.85]">
              {t("agora.philosophy.body")}
            </p>
          </Reveal>
        </div>
      </section>
      <Rule wide />

      {/* ============== XVI — THE EXIT ============== */}
      <section id="agora-chapter-13" data-testid="agora-s11" className="relative">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(197,160,89,0.18) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-40 md:py-56 text-center relative">
          <Reveal>
            <Numeral n={t("agora.s11.numeral")} />
            <Heading testid="agora-s11-title">{t("agora.s11.title")}</Heading>
            <p className="mt-10 font-serif italic text-gold/80 text-lg leading-[1.6]">
              {t("agora.s11.atmosphere")}
            </p>
          </Reveal>
          <Reveal delay={400}>
            <div className="mt-20 space-y-4 font-serif text-bone/95 text-2xl md:text-3xl leading-snug">
              <p>{t("agora.s11.line1")}</p>
              <p className="text-gold italic">{t("agora.s11.line2")}</p>
            </div>
          </Reveal>
          {/* outgoing stone-path hairline */}
          <div className="mt-24 mx-auto w-px h-32 bg-gradient-to-b from-gold/40 via-gold/15 to-transparent" aria-hidden />
        </div>
      </section>
    </main>
  );
}
